// Catalog.ID Client — Bitcash Wallet Sheet
// ============================================================================
// New in round 10 (registration-wallets §7): the account's bitcash wallet on
// paper. The entropy ceremony mints the key (§4.9), finalize puts it in the key
// bag as `bitcash_wallet` (§4.8), and the print step releases the registration
// voucher's bits into it (§4.5). This sheet is its only printed form.
//
// Both values on it are typeable, so the booklet's rule applies and there is NO
// QR — the decision the XRPL Master Key sheet made on 2026-09-03, for the same
// reason. The receive address is printed beside the key although it is a Worker
// record rather than a derivative of it: it is the one value a member hands to
// somebody else, and having it on the sheet means a member can be paid without
// opening anything.
//
// PAGE BUDGET: one A4 page (the assembler's one-page-per-builder contract). The
// binding variant is the DELEGATE one, whose account header carries three extra
// rows — Organization / Domain / Role, some 39 pt more than the personal
// header — so measure THAT one after any change here, with
// `apps/cid/dev/render-booklet.ts <dir> --png`. Measured on a 120 dpi raster of
// the sample kits, 2026-09-14, as ink used out of the 661.9 pt text box:
//   delegate variant (the binding one)    619.4 pt   93.6 %
//   organization variant                  587.6 pt   88.8 %
//   personal variant                      571.4 pt   86.3 %
// so the delegate sheet finishes 42.5 pt — about four lines — clear of the
// bottom. Every kit's page count matched its sheet count, which is what proves
// no builder spilled onto a second page.
//
// TRAP (inherited from the Authenticator Key sheet): pdfmake's bundled Roboto
// has no "→" glyph — an arrow prints as a missing-glyph box. Say it in words.
//
// Pure and node-importable (the sample renderer and the kit builders build it
// outside a browser), so no `?raw`/`?url` imports here and no colour anywhere.

import type { Content } from 'pdfmake/interfaces';
import { accountHeader, type AccountHeaderData } from './shared';
import { displayWalletKey, verifyWalletKey, verifyReceiveAddress } from '../../crypto/bitcash';

/** The sheet's title — also its row in the contents list, so the two cannot
 *  drift (`TOC_DESCRIPTIONS` in front-page.ts is keyed by it). */
export const BITCASH_WALLET_PAGE_TITLE = 'Bitcash Wallet';

export interface BitcashWalletPageData {
    header: AccountHeaderData;
    /** The wallet key, compact or display form — printed in the 34-character
     *  display form either way. */
    walletKey: string;
    /** The wallet's public receive address (`FUND-…`), when one has been minted.
     *  Absent in waived mode and on a resume that has not reached the address
     *  step: the sheet then prints without the block rather than failing. */
    receiveAddress?: string | null;
    /** Whether the registration's bits were released into this wallet before
     *  printing. False for a code that carried none, a release the ledger refused,
     *  or a registration with no code: the sheet then says the wallet was empty
     *  rather than promising money that is not there. */
    bitsPaidIn: boolean;
}

/** Space under each of the sheet's blocks. */
const BLOCK_GAP = 12;

export function buildBitcashWalletPage(data: BitcashWalletPageData): Content[] {
    if (!verifyWalletKey(data.walletKey)) {
        throw new Error('The Bitcash Wallet sheet needs a valid wallet key');
    }
    // No issuer line: a code carries no operator (docs/cid-governance.md M13), so
    // nothing on this sheet is read out of the code but the code itself.
    const key = displayWalletKey(data.walletKey)!;
    const address = data.receiveAddress ?? null;
    if (address !== null && !verifyReceiveAddress(address)) {
        throw new Error('The Bitcash Wallet sheet needs a valid receive address');
    }

    const content: Content[] = [
        { text: BITCASH_WALLET_PAGE_TITLE, style: 'pageTitle' },
        accountHeader(data.header),

        {
            text: 'This is the bitcash wallet that came with this membership. It holds bits — the '
                + 'unit Catalog services are paid in — and it was created on your own device during '
                + 'registration, alongside the account\'s other keys. '
                + (data.bitsPaidIn
                    ? 'The bits that came with the registration were paid into it at the moment this '
                        + 'kit was printed.'
                    : 'It was empty when this kit was printed; bits paid into it later show in your '
                        + 'Catalog.ID settings, under Wallets.'),
            style: 'value',
            margin: [0, 0, 0, BLOCK_GAP],
        },

        {
            unbreakable: true,
            stack: [
                { text: 'Wallet code', style: 'section' },
                { text: key, style: 'credential', margin: [0, 2, 0, 2] },
                {
                    text: 'The wallet itself. Anyone holding these characters can spend what is in '
                        + 'it, so it is kept here and encrypted on the account itself, and '
                        + 'nowhere else.',
                    style: 'note',
                },
            ],
            margin: [0, 0, 0, BLOCK_GAP],
        },
    ];

    if (address) {
        content.push({
            unbreakable: true,
            stack: [
                { text: 'Receive address', style: 'section' },
                { text: address, style: 'credential', margin: [0, 2, 0, 2] },
                {
                    text: 'Where other people pay you. It is safe to publish: it can receive bits '
                        + 'and it can do nothing else. You may also publish it as a claim on your '
                        + 'identity, so that anyone who can find your account can pay you.',
                    style: 'note',
                },
            ],
            margin: [0, 0, 0, BLOCK_GAP],
        });
    }

    content.push(
        {
            unbreakable: true,
            stack: [
                { text: 'How services are paid', style: 'section' },
                {
                    text: 'You do not type this code into anything. When a Catalog service needs '
                        + 'paying — storage, registry writes, mail — it asks Catalog.ID for a '
                        + 'spending token, and you approve that once per service. The token carries '
                        + 'its own daily and monthly limit, spends from this wallet, and can be '
                        + 'withdrawn at any time in Settings without touching the wallet itself.',
                    style: 'value',
                },
            ],
            margin: [0, 0, 0, BLOCK_GAP],
        },

        {
            unbreakable: true,
            stack: [
                { text: 'What it is not', style: 'section' },
                {
                    text: 'This is not an account that can be reset. There is no copy of the wallet '
                        + 'code anywhere but this sheet and your own account, and the operator holds '
                        + 'neither: a wallet whose code is lost with the rest of this kit cannot be '
                        + 'recovered, by anyone. It is also not tied to your name — whoever holds '
                        + 'the code can spend the bits, as with cash.',
                    style: 'value',
                },
            ],
            margin: [0, 0, 0, BLOCK_GAP],
        },

        {
            unbreakable: true,
            stack: [
                { text: 'Keep This Sheet Safe', style: 'section' },
                {
                    ul: [
                        'Keep it with the rest of this kit; do not photograph it or store scans of it.',
                        'The receive address above is the half you may hand out. The wallet code is not.',
                        'An account recovery hands this wallet over together with the rest of the '
                            + 'identity\'s keys.',
                    ],
                    style: 'note',
                },
            ],
        },
    );

    return content;
}
